/**
 * Life4Earth V2 — React hook for a user's PREVIOUS community/organization
 * roles. DEV-1368.
 *
 * Mirrors useApiCommunitySlotOccupancies exactly (cache-keyed by user GUID,
 * CACHE_UPDATE_EVENT-driven refetch). The companion cache key
 * `communityPreviousRoles:{userGuid}` is invalidated alongside
 * `communitySlotOccupancies:{userGuid}` on resign/revoke (see
 * workflowSideEffects) so a just-ended role moves from current → previous
 * without a manual reload.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchCommunityPreviousRoles } from "./communityRoleSlots";
import type { CommunityPreviousRoleResponse } from "./communityRoleSlots";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiCommunityPreviousRolesResult {
  previousRoles: CommunityPreviousRoleResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  refetch: () => void;
}

export function useApiCommunityPreviousRoles(
  userGuid: string | null | undefined,
): UseApiCommunityPreviousRolesResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiCommunityPreviousRolesResult>({
    previousRoles: [],
    loading: !!userGuid,
    error: null,
    source: userGuid ? "loading" : "empty",
    refetch: () => {},
  });

  const cacheKey = userGuid ? `communityPreviousRoles:${userGuid}` : null;

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !userGuid) return;
    try {
      const rows = await fetchForKey<CommunityPreviousRoleResponse[]>(
        cacheKey!,
        () => fetchCommunityPreviousRoles(userGuid),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        previousRoles: rows,
        loading: false,
        error: null,
        source: rows.length > 0 ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, [userGuid, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (userGuid) void refetch();

    const onCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === cacheKey) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [userGuid, cacheKey, refetch]);

  return data;
}
